import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organizationService';
import { Organization, OrganizationInvitation } from '../../types';

const AcceptInvitationPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitations, setInvitations] = useState<{
    organization: Organization;
    invitation: OrganizationInvitation;
  }[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const findInvitations = async () => {
      if (!user) {
        setError('Vous devez être connecté pour voir vos invitations.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Attendre un peu pour permettre la propagation du token Firebase
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        console.log('Recherche des invitations pour:', user.email);
        
        if (!user.email) {
          setError('Aucune adresse email associée à votre compte.');
          setLoading(false);
          return;
        }

        try {
          const results = await organizationService.findOrganizationsByInvitationEmail(user.email);
          
          console.log('Invitations trouvées:', results);
          
          if (results.length === 0) {
            setError('Aucune invitation en attente trouvée pour votre adresse email.');
          } else {
            setInvitations(results);
          }
        } catch (permissionErr) {
          console.error('Erreur de permission lors de la recherche d\'invitations:', permissionErr);
          
          // Si nous obtenons une erreur de permission, réessayer après un délai plus long
          console.log('Nouvelle tentative après un délai...');
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          try {
            const results = await organizationService.findOrganizationsByInvitationEmail(user.email);
            
            if (results.length === 0) {
              setError('Aucune invitation en attente trouvée pour votre adresse email.');
            } else {
              setInvitations(results);
            }
          } catch (retryErr) {
            console.error('Erreur lors de la nouvelle tentative:', retryErr);
            throw retryErr; // Relancer pour être capturé par le catch externe
          }
        }
      } catch (err) {
        console.error('Erreur lors de la recherche d\'invitations:', err);
        if (err instanceof Error && err.message.includes('permission')) {
          setError('Impossible de vérifier les invitations. Veuillez vous assurer que vous êtes connecté avec l\'adresse email qui a reçu l\'invitation.');
        } else {
          setError(err instanceof Error ? err.message : 'Échec de la recherche d\'invitations');
        }
      } finally {
        setLoading(false);
      }
    };

    findInvitations();
  }, [user]);

  const handleAccept = async (organizationId: string, invitationId: string) => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      console.log('Acceptation de l\'invitation:', invitationId);
      await organizationService.acceptInvitation(
        organizationId,
        invitationId,
        user.id
      );
      
      // Attendre un peu pour laisser le temps à l'assignation de se faire
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Rediriger vers la page appropriée en fonction du rôle
      const invitation = invitations.find(
        inv => inv.organization.id === organizationId && inv.invitation.id === invitationId
      );
      
      if (invitation) {
        window.location.href = invitation.invitation.role === 'User' ? '/driver' : '/';
      } else {
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Erreur lors de l\'acceptation de l\'invitation:', err);
      setError(err instanceof Error ? err.message : 'Échec de l\'acceptation de l\'invitation');
      setLoading(false);
    }
  };

  const handleDecline = async (organizationId: string, invitationId: string) => {
    try {
      setLoading(true);
      setError(null);

      await organizationService.declineInvitation(
        organizationId,
        invitationId
      );
      
      // Mettre à jour la liste des invitations
      setInvitations(invitations.filter(
        inv => !(inv.organization.id === organizationId && inv.invitation.id === invitationId)
      ));
      
      setLoading(false);
    } catch (err) {
      console.error('Erreur lors du refus de l\'invitation:', err);
      setError(err instanceof Error ? err.message : 'Échec du refus de l\'invitation');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Invitations en attente
        </Typography>
        
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : invitations.length === 0 ? (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              Aucune invitation en attente trouvée pour votre adresse email.
            </Alert>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/')}
              >
                Retour à l'accueil
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 3 }}>
            {invitations.map((inv) => (
              <Card key={inv.invitation.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {inv.organization.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Rôle: {inv.invitation.role}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fonction: {inv.invitation.functionTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Invité le: {inv.invitation.createdAt.toDate().toLocaleDateString()}
                  </Typography>
                  {inv.invitation.vehicleId && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Un véhicule vous sera assigné lors de l'acceptation de cette invitation.
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDecline(inv.organization.id, inv.invitation.id)}
                    disabled={loading}
                  >
                    Refuser
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleAccept(inv.organization.id, inv.invitation.id)}
                    disabled={loading}
                  >
                    Accepter
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AcceptInvitationPage;